import { forwardRef } from "react";

import { Loader2 } from "lucide-react";

import { cn } from "../../../lib/utils";

/**
 * THE FACILITATOR OS BUTTON. One hierarchy, four roles, two sizes.
 *
 * ===================== WHY A COMPONENT AND NOT A CLASS RECIPE ==============
 * A recipe (`buttonClasses(variant)`) would consolidate the strings and leave
 * every other divergence in place: the busy state that exists on exactly one
 * button in the OS, the `type="button"` that a hand-written `<button>` inside a
 * form silently omits, and the `aria-busy` nobody remembers. Those are
 * behaviours, and they are the ones the audit found missing, so the primitive
 * has to own them. The recipe form is still exported as `buttonClasses` for the
 * one case a component cannot serve: a control this file does not render.
 *
 * ===================== THE PRIMARY FILL IS CHARLOTTE, NOT PERSIMMON ========
 * The audit's fourth system defect: primary fill was surface-dependent rather
 * than role-dependent. Manage confirmed in persimmon, Coach and Prep and Review
 * confirmed in charlotte, so the same colour meant "commit this plan"
 * (`TopologyWorkspace`), "this is a one-way door" (`SquadArchiveDialog`), "needs
 * your attention" (`AttentionStage`, the sidebar badge) and "this errored"
 * (Review). A teacher could not learn one rule. Charlotte takes ordinary
 * primary because three of the four surfaces already spelled it that way and
 * because it leaves persimmon free to mean exactly one thing: consequence.
 *
 * The archive confirm keeps persimmon and is NOT a regression to fix. It is
 * earned divergence 1 in `shared-grammar.md` 6.2: a one-way door on a surface
 * whose other confirms are reversible. It reaches persimmon through
 * `variant="destructive"` rather than through a hand-written class, so the
 * weight is now a stated role instead of a coincidence of copy-paste.
 *
 * ===================== THE INK ON A FILL IS MEASURED, NOT CHOSEN ===========
 * Every persimmon button in Manage shipped `text-white`, which measures 3.05:1
 * against `#FF5A5F` and fails WCAG AA for body text. `text-background-blackpearl`
 * measures 5.97:1 on the same fill and 13.85:1 on charlotte. Both numbers are
 * pinned in `Button.test.jsx` so a later palette edit cannot walk them down.
 *
 * ===================== TWO DISABLED TREATMENTS, DERIVED NOT CHOSEN =========
 * Five shipped (`opacity-38` x26, the four-part fill swap x4, `opacity-40` x6,
 * `opacity-50` x5, a hardcoded `#FF5A5F/50` x3), and 40 and 50 are off-token:
 * `state.disabled` is 0.38. Two survive, and which one applies is derived from
 * the variant's shape rather than picked per call site. A FILLED button swaps
 * its fill for `background-cloudburst` and its ink for `lv3` (10.15:1, still
 * readable) because fading a filled pill to 38% dims the label into the pill. An
 * UNFILLED button takes `opacity-38`, the token, because giving a text button a
 * grey pill invents an affordance the enabled state does not have.
 *
 * ===================== CASING IS NOT ENFORCED HERE, DELIBERATELY ===========
 * The house rule is lowercase copy with capitalisation applied in CSS, and the
 * obvious move is a `lowercase` class on the base. It is wrong here: submit
 * labels in Manage interpolate squad and student names (`roomCopy`), and
 * lowercasing a person's name in a confirm button is a worse defect than the one
 * it fixes. Case stays with the copy modules that already own it.
 */

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-fast focus-visible:focus-ring disabled:cursor-not-allowed";

/** Filled variants carry their own ink; unfilled variants inherit the ramp. */
const FILLED_DISABLED =
  "disabled:bg-background-cloudburst disabled:text-lv3 disabled:opacity-100 disabled:shadow-none";
const UNFILLED_DISABLED = "disabled:opacity-38";

const VARIANT = {
  primary: `bg-charlotte text-background-blackpearl shadow-elevation-2 hover:shadow-glow-charlotte ${FILLED_DISABLED}`,
  destructive: `bg-persimmon text-background-blackpearl shadow-elevation-2 hover:shadow-glow-persimmon ${FILLED_DISABLED}`,
  secondary: `border border-white/15 text-lv1 hover:bg-state-hover ${UNFILLED_DISABLED}`,
  tertiary: `text-lv3 hover:bg-state-hover hover:text-lv1 ${UNFILLED_DISABLED}`,
};

const SIZE = {
  sm: "px-3 py-1 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-2.5 text-base",
};

/** The recipe, for controls this component does not render. */
export const buttonClasses = ({
  variant = "primary",
  size = "md",
  className = "",
} = {}) =>
  cn(
    BASE,
    VARIANT[variant] ?? VARIANT.primary,
    SIZE[size] ?? SIZE.md,
    className,
  );

/**
 * THE `disabled:` TREATMENT, REAPPLIED BY HAND, FOR THE LOADING-ONLY CASE.
 *
 * Real browsers unfocus an element the instant it gains the `disabled`
 * attribute, synchronously, moving `document.activeElement` to `<body>`.
 * Verified live in Chromium: focus a button, set `.disabled = true`, and
 * `document.activeElement` is `BODY`. jsdom does not reproduce this (the
 * suite's own loading test asserted `toBeDisabled()` and never caught it),
 * which is exactly how this shipped: the FE-6/8 defect class (focus dropped
 * to `<body>`) arriving through a new door. `TopologyWorkspace` passes
 * `loading={intent.busy}` to two commit buttons; a facilitator who presses
 * Enter on a focused "commit" loses their keyboard position to `<body>` the
 * instant the request starts, and a screen reader user loses the control
 * from the tab order entirely mid-action.
 *
 * The fix keeps the node focusable while `loading` (and not also explicitly
 * `disabled`): `aria-disabled` replaces the native attribute, the click
 * handler blocks the action by hand, and this function reapplies the exact
 * same visual treatment the `disabled:` variant would have produced, stripped
 * of its pseudo-class prefix so it applies unconditionally. RED: the class
 * list for a loading-only button would stop matching the `disabled`-prop
 * button's own treatment.
 */
const loadingOnlyClasses = (variant) =>
  (VARIANT[variant] ?? VARIANT.primary)
    .split(" ")
    .filter((c) => c.startsWith("disabled:"))
    .map((c) => c.slice("disabled:".length))
    .concat(["pointer-events-none", "cursor-not-allowed"])
    .join(" ");

/**
 * THE ICON IS LEADING AND THAT IS THE WHOLE OPTION SET. Review led with icons,
 * Manage and Coach had none, and one control put a text affordance trailing. A
 * `iconPosition` prop would preserve the disagreement under a nicer name.
 *
 * THE REF IS FORWARDED BECAUSE THE OS FOCUSES BUTTONS. `CommitOutcome` pulls
 * focus to its acknowledge control the moment a one-shot password appears, and
 * `SquadArchiveDialog` deliberately focuses cancel rather than confirm on a
 * one-way door. Both hold a ref to the DOM node. A function component would drop
 * it with a console warning and no test failure, and the focus discipline those
 * two files argue for in their docblocks would quietly stop happening.
 */
/**
 * `blocked` SEPARATES "UNAVAILABLE RIGHT NOW" FROM "WORKING" (FE-9.4).
 *
 * Manage's commit controls used to pass `loading={coordinator.busy}`, an
 * AGGREGATE flag, which did two jobs at once: it made the control inert while
 * any other mutation was in flight, and it spun a spinner on it. The second half
 * is a defect (CodeRabbit on PR #380, carried here): pressing preview spun the
 * commit button, and a settled reset outcome left buttons spinning until it was
 * released. Splitting the flags naively would have LOST the first half, and the
 * control would have looked live during somebody else's write.
 *
 * So `loading` now means only "this control's own operation is running" and
 * `blocked` means "this control cannot act right now". Both produce the same
 * inert treatment, deliberately: `aria-disabled` rather than the native
 * attribute, so a focused control is not yanked out of the tab order mid action,
 * with the click blocked by hand. Only `loading` renders the spinner.
 */
export const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    blocked = false,
    disabled = false,
    icon = null,
    as = "button",
    className = "",
    children,
    onClick,
    ...rest
  },
  ref,
) {
  // Loading is transient and the OS keys off `disabled` for the permanent
  // case. Only the loading-only path (not also explicitly `disabled`) gets
  // the aria-disabled treatment; a caller who passes `disabled` outright
  // gets the plain native attribute, unchanged.
  const loadingOnly = (loading || blocked) && !disabled;

  /* THE ANCHOR IS UNAVAILABLE UNDER BOTH FLAGS, NOT NEITHER. `disabled` never
     reached the anchor (the attribute means nothing on an `<a>`) and `loading`
     was never read there, so the Review escape hatch stayed activatable through
     its own in-flight request. Both states now route through the same hand-rolled
     guard the loading-only BUTTON already uses, for the same reason: the anchor
     has no native attribute to lean on either.

     `href` STAYS and `tabIndex` is NOT touched. Removing `href` demotes the node
     out of the screen reader's link list, and pulling the control out of the tab
     order mid-action is the FE-6/8 defect class (focus lost to `<body>`) arriving
     through the anchor door. `aria-disabled` states the condition; the click
     guard enforces it. */
  const isAnchor = as === "a";
  const inert = isAnchor ? disabled || loading || blocked : loadingOnly;

  const classes = cn(
    buttonClasses({ variant, size, className }),
    inert && loadingOnlyClasses(variant),
  );

  /* THE BUSY BUTTON MOVES. Exactly one button in the OS swapped a spinner in
     while it worked; every other one went grey and sat there, so a teacher
     committing a twenty-person reorganization got no motion at all. The spinner
     replaces the icon slot rather than joining it, so the label does not shift
     when work starts. */
  const leading = loading ? (
    <Loader2
      aria-hidden="true"
      className="h-4 w-4 animate-spin motion-reduce:animate-none"
    />
  ) : (
    icon
  );

  /* THE CLICK IS BLOCKED BY HAND WHILE INERT, because the node is not natively
     `disabled` in either inert case: a native `disabled` button drops keyboard
     focus to `<body>` the instant it is set, which is worse than the
     double-submit this guard exists to prevent, and an anchor cannot carry the
     attribute at all. `preventDefault` is what stops the anchor NAVIGATING;
     returning early is what stops the caller's handler firing. */
  const handleClick = (event) => {
    if (inert) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  if (isAnchor) {
    return (
      <a
        {...rest}
        ref={ref}
        className={classes}
        aria-disabled={inert ? "true" : undefined}
        aria-busy={loading ? "true" : undefined}
        onClick={handleClick}
      >
        {leading}
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      // Stated, never inferred. A hand-written `<button>` inside a form defaults
      // to submit, and three of the sites this replaces sit inside forms.
      type={rest.type ?? "button"}
      disabled={disabled}
      aria-disabled={loadingOnly ? "true" : undefined}
      aria-busy={loading ? "true" : undefined}
      className={classes}
      onClick={handleClick}
      {...rest}
    >
      {leading}
      {children}
    </button>
  );
});

export default Button;
